import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { API_GATEWAY_PATH, IFilterResponse } from '@impact/data';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, skip, takeUntil } from 'rxjs/operators';
import { Environment, ENVIRONMENT } from '../environment/environment';
import {
    FILTER_FACET,
    IFilterFacetPair,
    IParamsOfArrays,
} from './filter.models';

enum FilterValues {
    TAKE = 21,
}

@Injectable({
    providedIn: 'root',
})
export class FilterService {
    private cancel: Subject<void> = new Subject<void>();
    private exchangeCarValue$ = new BehaviorSubject(0);
    private facets$ = new BehaviorSubject<IParamsOfArrays>({});
    private facetsSubscription: Subscription;
    private filterResponseSubject$ = new BehaviorSubject<IFilterResponse | null>(null);
    private from = 0;
    private initialFilter$ = new Subject<IFilterResponse>();
    private initialFilterParams$ = new BehaviorSubject<IParamsOfArrays>({});
    private initialParamsNotRelatedToFilterPage: Params;
    private initialSize: number;
    private queryString$ = new BehaviorSubject<string>('');
    private queryStringSubscription: Subscription;
    private rootUrl: string;
    private showCashPriceDefault = this.environment.site === "Motorpoint";
    private showLeasingOnly: boolean;
    private sorting$ = new BehaviorSubject<string | null>(null);
    private validFilterFacetKeys: string[];

    carsVisibleCount = FilterValues.TAKE; // Used for Skeleton-grid element only. Will be updated everytime facets are collected.
    filterUrlChange$ = new BehaviorSubject('')
    isInitializing$ = new BehaviorSubject(true);
    isLoading$ = new BehaviorSubject(false);
    isLoadingMore$ = new BehaviorSubject(false);
    showCashPrice$ = new BehaviorSubject(this.showCashPriceDefault);
    take = FilterValues.TAKE;

    get activeFacets() {
        return this.facets$.asObservable();
    }

    get activeSorting() {
        return this.sorting$.asObservable();
    }

    get enrichedActiveFacets() {
        // Return a enriched active facet key list.
        return this.facets$.pipe(
            map((_currentActiveFacets) => {
                const _activeFacets: IFilterFacetPair[] = [];
                Object.entries(_currentActiveFacets).forEach(([facet, keys]: [string, string | string[]]) => {
                    [...(Array.isArray(keys) ? keys : [keys])].forEach((key: string) => {
                        switch (facet) {
                            case 'price':
                            case 'mileage':
                            case 'year':
                            case 'leasingPricePrivateFinancial':
                            case 'leasingPricePrivateOperational':
                            case 'leasingPriceBusinessOperational':
                            case 'leasingPriceBusinessFinancial':
                                _activeFacets.push({ facet, option: { key } });
                                break;

                            default: {
                                const facetMatch = this.filterResponseSubject$.value?.facets?.[facet]?.items?.find(
                                    (item) => item.key?.toLowerCase() === key?.toLowerCase()
                                );
                                if (facetMatch) {
                                    // Create an enriched facet:
                                    _activeFacets.push({ facet, option: facetMatch });
                                } else {
                                    // no facet/filterdata match available, mock a facet:
                                    _activeFacets.push({
                                        facet,
                                        option: {
                                            key,
                                            label: key,
                                        },
                                    });
                                }
                            }
                        }
                    });
                });
                return _activeFacets;
            })
        );
    }
    
    get exchangeCarValue() {
        return this.exchangeCarValue$.asObservable();
    }

    get filterData() {
        return this.filterResponseSubject$.asObservable();
    }

    get initialFilter() {
        return this.initialFilter$.asObservable();
    }

    get initialFilterParams() {
        return this.initialFilterParams$.asObservable();
    }

    get isInitializing() {
        return this.isInitializing$.asObservable();
    }

    constructor(
        private http: HttpClient,
        private location: Location,
        private router: Router,
        @Inject(ENVIRONMENT) private environment: Environment,
    ) {}

    setLeasingOnlyBoolean(value: boolean) {
        this.showLeasingOnly = value;
    }

    initFilter(rootUrl: string, { page, sort, initialFilters, ...params }: Params & { initialFilters?: IParamsOfArrays}) {
        this.isInitializing$.next(true);
        // Reset any old states - maybe its a revisit to the filterpage.
        this.resetFilterState();
        // Set all state variables and facets from initial URL params
        this.rootUrl = rootUrl;
        this.initialSize = parseInt(page ?? 1, 10) * this.take;
        this.from = this.initialSize - this.take;
        this.setSorting(sort, false);

        // If page sets showLeasingOnly, we add leasing to the initial filters object
        if (this.showLeasingOnly) {
            initialFilters = initialFilters ?? {};
            initialFilters = {
                'purchaseOption.keyword': ['Leasing'],
                ...initialFilters,
            };
        }

        this.initialFilterParams$.next(initialFilters ?? {});

        // Fetching an initial "clean" filter / without any params, to get all possible facets,
        // for us to then validate which query params are actually valid filter facets.
        this.fetchFilterResponse(this.initialSize).subscribe((cleanFilterResponse) => {
            let validatedParams: IParamsOfArrays | null = null;

            if (cleanFilterResponse?.facets) {
                const facetNameWhiteList = [FILTER_FACET.MODEL, 'page']; // Because model is currently only on the filterResponse if you send "make" as a param. and therefor not present on a "clean" call.
                this.validFilterFacetKeys = [...facetNameWhiteList, ...Object.keys(cleanFilterResponse?.facets)];

                validatedParams = Object.keys(params)
                    .filter((key) => this.validFilterFacetKeys.includes(key))
                    .reduce((obj: IParamsOfArrays, key) => {
                        obj[key] = params[key];
                        return obj;
                    }, {});

                this.initialParamsNotRelatedToFilterPage = Object.keys(params)
                    .filter((key) => !this.validFilterFacetKeys.includes(key))
                    .reduce((obj: IParamsOfArrays, key) => {
                        obj[key] = params[key];
                        return obj;
                    }, {});
            }

            const initialFilterHasNoValidParams = Object.keys(validatedParams || {}).length === 0;
            if (initialFilterHasNoValidParams) {
                // Because the initial filter query has no valid params, we can keep the initially fetched "clean" filter.
                this.setFilterData(cleanFilterResponse);
                // Reset initialParamsNotRelatedToFilterPage
                this.initialParamsNotRelatedToFilterPage = {};
            }

            this.applyParamsAsFacets({
                ...(validatedParams || params),
            });

            // State is now ready, init subscriptions
            this.initSubscriptions(initialFilterHasNoValidParams);

            this.initialFilter$.next(cleanFilterResponse);
            this.isInitializing$.next(false);
        });
    }

    setFacet(facetPair: IFilterFacetPair, overwriteOrRemove?: boolean) {
        const facetName = facetPair.facet; // Do not set to lowercase, will make query param requests fail
        const facetValue = facetPair.option.key; // Do not set to lowercase, will make query param requests fail

        // Get all current facets, and extract/filter the new facet
        const { [facetName]: prevFacetValue, ...rest } = this.facets$.value;

        const facetAlreadySet = prevFacetValue?.length === 1 && facetPair?.option?.selected;

        const nextFacetState = {
            ...rest,
            // if we already have this facet, and the new facet has option selected as true. remove it / return nothing.
            ...(facetAlreadySet || overwriteOrRemove
                ? {} // will disappear when unwrapped
                : {
                      [facetName]: [
                          ...(prevFacetValue?.filter((f) => f !== facetValue) || []), // setting all other values this facet may already have.
                          ...(facetPair?.option?.selected ? [] : [facetValue || '']), // set or remove new facet, depending on it's "selected" value.
                      ],
                  }),
            ...(overwriteOrRemove
                ? {
                      [facetName]: [...[facetValue || '']],
                  }
                : {}),
        };

        // Setting new facet state
        this.facets$.next(nextFacetState);
    }

    setFacets(facetPair: IFilterFacetPair[]) {
        const result = facetPair.reduce((obj: Map<string, string[]>, key) => {
            if (key.facet && key.option.key && !this.initialFilterParams$.value[key.facet]) {
                obj.set(key.facet, [...obj.get(key.facet) ?? [], key.option.key]);;
            }

            return obj;
        }, new Map())

        // Setting new facet state
        this.facets$.next(
            Object.fromEntries(result)
        );
    }

    resetFacets() {
        this.facets$.next({});
    }

    loadMore() {
        this.isLoadingMore$.next(true);
        this.from = this.filterResponseSubject$.value?.hits?.length || 0; // Set from, to the current amount og results
        this.updateFilter(this.facets$.value, true); // Then initiate new update
    }

    loadMoreUrl() {
        return `${this.rootUrl}?${this.queryString$.value}&page=${(this.from/this.take) + 2}`;
    }

    setSorting(sorting: string, shouldUpdateFilter = true) {
        this.sorting$.next(sorting);
        if (shouldUpdateFilter) {
            this.updateFilter(this.facets$.value);
        }
    }

    setExchangeCarValue(val: number) {
        this.exchangeCarValue$.next(this.exchangeCarValue$.value === val ? 0 : val);
    }

    private initSubscriptions(skipInitialUpdate?: boolean) {
        // If any facet changes - update filter state.
        this.facetsSubscription = this.facets$.pipe(skip(skipInitialUpdate ? 1 : 0)).subscribe((facets) => this.updateFilter(facets));

        // Updated filter, will result in new querystring, which til run new filter fetch
        this.queryStringSubscription = this.queryString$.pipe(skip(skipInitialUpdate ? 1 : 0)).subscribe(() => this.onQueryUpdate());
    }

    private destroySubscriptions() {
        this.facetsSubscription?.unsubscribe();
        this.queryStringSubscription?.unsubscribe();
    }

    private resetFilterState() {
        this.from = 0;
        this.destroySubscriptions();
        this.filterResponseSubject$.next(null);
        this.facets$.next({});
        this.queryString$.next('');
        this.initialFilterParams$.next({});
        this.validFilterFacetKeys = [];
        this.initialParamsNotRelatedToFilterPage = {};
    }

    private onQueryUpdate() {
        // If initial fetch, check for initialSize
        const takeInitialSize = this.filterResponseSubject$.value ? undefined : this.initialSize;

        this.fetchFilterResponse(takeInitialSize).subscribe(
            (res) => (this.from ? this.appendResults(res) : this.setFilterData(res)),
            () => this.handleFetchError()
        );
    }

    private applyParamsAsFacets(params: Params) {
        const result: IFilterFacetPair[] = [];
        Object.entries(params).forEach(([facet, keys]: [string, string | string[]]) => {
            [...(Array.isArray(keys) ? keys : [keys])].forEach((key: string) => result.push({ facet, option: { key } }));
        });
        this.setFacets(result);
    }

    private setFilterData(filterResponse: IFilterResponse) {
        this.carsVisibleCount = filterResponse.hits?.length || this.take;
        this.filterResponseSubject$.next(filterResponse);
        this.isLoading$.next(false);
        this.isLoadingMore$.next(false);
    }

    private appendResults({ hits: newHits, ...filterResponse }: IFilterResponse) {
        if (!newHits) return;
        this.setFilterData({ ...filterResponse, hits: [...(this.filterResponseSubject$.value?.hits || []), ...newHits] });
    }

    private updateFilter(facets: IParamsOfArrays, loadMore?: boolean) {
        if (!loadMore) this.from = 0; // When facets change. reset from$ count.

        const sort = this.sorting$.value ? { sort: [this.sorting$.value] } : undefined;
        const facetsAndSorting = { ...facets, ...sort };

        const queryParams: string[][] = [];
        Object.entries(facetsAndSorting)?.forEach(([facet, keys]) => {
            switch (facet) {
                default:
                    keys?.forEach((key) => queryParams.push([facet, key || '']));
                    break;
            }
        });

        // Encode params if not a make or model
        const encodedPairs = queryParams.map((pair) =>
            pair.map((param) => encodeURIComponent(param)).join('=')
        );

        const queryString = encodedPairs.length ? `&${encodedPairs.join('&')}` : '';
        this.queryString$.next(queryString);

        const size = (this.from / this.take) + 1 > 1 ? { page: [`${(this.from / this.take) + 1}`] } : undefined;
        this.mergeParamsIntoUrlQuery({ ...facetsAndSorting, ...size });
    }

    // Apply pretty urls for makes and models
    private mergeParamsIntoUrlQuery(facets: IParamsOfArrays) {
        const route = this.router.serializeUrl(
            this.router.createUrlTree([], {
                /* Removed unsupported properties by Angular migration: skipLocationChange. */
                queryParams: {
                    ...facets,
                    ...(this.initialParamsNotRelatedToFilterPage || {}),
                }
            })
        );

        // Emit filter url to listening components (filter-page-seo-links)
        this.filterUrlChange$.next(route);

        this.location.go(route);

        // Reset initialParamsNotRelatedToFilterPage
        this.initialParamsNotRelatedToFilterPage = {};
    }

    private fetchFilterResponse(take?: number) {
        this.cancel.next(); // Cancel any pending "old" requests.
        this.isLoading$.next(true);

        let filter = `from=${this.from}&take=${take || this.take}`;

        filter += `${this.queryString$.value}`;

        const initialFilter = this.convertObjectToQueryString(this.initialFilterParams$.value);
        if (initialFilter) {
            filter += `&${initialFilter}`;
        }

        if(this.environment.site === "Motorpoint") {
            return this.http
            .get<IFilterResponse>(`/${API_GATEWAY_PATH}/Filter/FilterMotorPoint?${filter}`)
            .pipe(takeUntil(this.cancel));
        } else {
            return this.http
            .get<IFilterResponse>(`/${API_GATEWAY_PATH}/filter/filter?${filter}`)
            .pipe(takeUntil(this.cancel));
        }

    }

    // Fetch filter results (outside of filter page, e.g. spots etc)
    public independentFetchFilter(queryString: string, take?: number, from = 0): Observable<IFilterResponse> {
        if (queryString.indexOf('?') === 0) {
            queryString = queryString.substring(1);
        }

        if (!take) {
            take = 35;
        }

        if(this.environment.site === "Motorpoint") {
            return this.http.get<IFilterResponse>(`/${API_GATEWAY_PATH}/Filter/FilterMotorPoint?from=${from}&take=${take}&${queryString}`);
        } else {
             return this.http.get<IFilterResponse>(`/${API_GATEWAY_PATH}/filter/filter?from=${from}&take=${take}&${queryString}`);
        }

    }

    public getCarsVisibleCount() {
        return this.carsVisibleCount;
    }

    private handleFetchError() {
        this.isLoading$.next(false);
        this.isLoadingMore$.next(false);
    }

    convertObjectToQueryString(obj: Record<string, string | string[]>): string {
        const keyValuePairs: string[] = [];

        const keys = Object.keys(obj);

        for (const key of keys) {
          const value = obj[key];

          // Handle both string and array of strings values
          if (Array.isArray(value)) {
            for (const val of value) {
              keyValuePairs.push(`${key}=${val}`);
            }
          } else {
            keyValuePairs.push(`${key}=${value}`);
          }
        }

        return keyValuePairs.join('&');
      }
}
